import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	IAddFileProps,
	IEditFileProps,
	IGenerateAnnotationProps,
	IModifyPhrasesProps,
	IQasDocumentState,
	IReplaceTextProps,
	IResponseAddingFile,
	IResponseDocumentData,
	IResponseFileData,
	IResponseGenerateAnnotation,
	IResponseReplaceText,
} from "@/types/qas/qasDocumentTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasDocumentState = {
	filesList: {
		data: null,
		numberOfDocumentsInCategories: {},
		status: RequestStatus.IDLE,
	},
	document: {
		data: null,
		glossary: {},
		fileId: null,
		fileName: null,
		category: null,
		status: RequestStatus.IDLE,
		useAnchors: false,
		useAnnotation: false,
		useHead: false,
		useTitle: false,
		annotation: "",
	},
	addingFile: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
		progress: 0,
	},
	editingFile: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		changesNameOrCategory: false,
	},
	deletingFile: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	annotationGeneration: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		text: "",
	},
	replaceInDocument: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		replacements: null,
	},
	editingPhrases: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка документов
export const getFilesList = createAsyncThunk("qasDocument/list", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseFileData | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.DOC_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение документа
export const getFile = createAsyncThunk("qasDocument/get", async (id: string, { rejectWithValue, signal }) => {
	try {
		const response: AxiosResponse<IResponseDocumentData | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.DOC_GET}/${id}`, {
			signal,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление документа
export const addFile = createAsyncThunk(
	"qasDocument/add",
	async ({ title, categoryId, formData, useAnchors, useAnnotation, useHead, useTitle, annotation }: IAddFileProps, { dispatch, rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseAddingFile> = await axiosApi.post(
				`${URL_QAS}/${ENDPOINTS_QAS.DOC_ADD}`,
				{
					text: formData.get("file"),
					title,
					category: categoryId,
					useHead: useHead ? 1 : 0,
					useTitle: useTitle ? 1 : 0,
					useAnnotation: useAnnotation ? 1 : 0,
					useAnchors: useAnchors ? 1 : 0,
					annotation,
				},
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
					onUploadProgress: (progressEvent) => {
						if (progressEvent.progress) dispatch(changeProgressAddingFile(progressEvent.progress));
					},
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// изменение документа
export const editFile = createAsyncThunk("qasDocument/edit", async ({ fileId, data }: IEditFileProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.DOC_EDIT}/${fileId}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление документа
export const deleteFile = createAsyncThunk("qasDocument/delete", async (id: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_QAS}/${ENDPOINTS_QAS.DOC_DELETE}/${id}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// генерация аннотации
export const generateAnnotation = createAsyncThunk(
	"qasDocument/generateAnnotation",
	async ({ docId, model, templateId }: IGenerateAnnotationProps, { signal, rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseGenerateAnnotation> = await axiosApi.post(
				`${URL_QAS}/${ENDPOINTS_QAS.DOC_GENERATE_ANNOTATION}/${docId}`,
				{
					model,
					template: templateId,
				},
				{ signal }
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// поиск и замена текста во всех документах
export const replaceTextInAllDocuments = createAsyncThunk(
	"qasDocument/replaceTextInAllDocuments",
	async ({ search, replace, categoryId }: IReplaceTextProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseReplaceText> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.DOC_REPLACE}`, {
				search,
				replace,
				category: categoryId,
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// замена фраз в документе
export const modifyPhrases = createAsyncThunk("qasDocument/modifyPhrases", async ({ phrases }: IModifyPhrasesProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseReplaceText> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.DOC_PHRASES_EDIT}`, {
			phrases: JSON.stringify(phrases),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasDocumentSlice = createSlice({
	name: "qasDocument",
	initialState,
	reducers: {
		// изменение прогресса добавления файла в %
		changeProgressAddingFile: (state, action: PayloadAction<number>) => {
			state.addingFile.progress = Math.round(action.payload * 100);
		},
		// добавление id активного файла
		addActiveFileId: (state, action: PayloadAction<string>) => {
			state.document.fileId = action.payload;
		},
		// изменение имени активного файла
		changeActiveFileName: (state, action: PayloadAction<{ fileName: string; change: boolean }>) => {
			state.document.fileName = action.payload.fileName;
			if (action.payload.change) state.editingFile.changesNameOrCategory = true;
		},
		// изменение категории активного файла
		changeActiveFileCategory: (state, action: PayloadAction<{ categoryName: string; change: boolean }>) => {
			state.document.category = action.payload.categoryName;
			if (action.payload.change) state.editingFile.changesNameOrCategory = true;
		},
		// изменение документа
		editDocument: (state, action: PayloadAction<string>) => {
			state.document.data = action.payload;
		},
		// изменение аннотации
		editAnnotation: (state, action: PayloadAction<string>) => {
			state.document.annotation = action.payload;
		},
		// изменение булевых данных документа
		changeDocumentBooleanValues: (
			state,
			action: PayloadAction<{ field: keyof Pick<IResponseDocumentData, "useHead" | "useTitle" | "useAnnotation" | "useAnchors">; value: boolean }>
		) => {
			state.document[action.payload.field] = action.payload.value;
		},
		// ------- глоссарий ------- \\
		// изменение ячейки сокращения таблицы глоссария
		editGlossaryDataCellAbbreviation: (state, action: PayloadAction<{ abbreviationOld: string; abbreviationNew: string; transcript: string }>) => {
			delete state.document.glossary[action.payload.abbreviationOld];
			state.document.glossary[action.payload.abbreviationNew] = action.payload.transcript;
		},
		// изменение ячейки расшифровки таблицы глоссария
		editGlossaryDataCellTranscript: (state, action: PayloadAction<{ abbreviation: string; transcript: string }>) => {
			state.document.glossary[action.payload.abbreviation] = action.payload.transcript;
		},
		// добавления строки в таблицу глоссария
		addRow: (state, action: PayloadAction<{ abbreviation: string; transcript: string }>) => {
			state.document.glossary[action.payload.abbreviation] = action.payload.transcript;
		},
		// добавление строк в таблицу глоссария
		addRows: (state, action: PayloadAction<Record<string, string>>) => {
			state.document.glossary = Object.assign(state.document.glossary, action.payload);
		},
		// замена глоссария
		replaceGlossary: (state, action: PayloadAction<Record<string, string>>) => {
			state.document.glossary = action.payload;
		},
		// удаление строк из таблицы глоссария
		deleteRows: (state, action: PayloadAction<string[]>) => {
			for (const abbreviation in state.document.glossary) {
				if (action.payload.includes(abbreviation)) {
					delete state.document.glossary[abbreviation];
				}
			}
		},
		// изменение данных в списке документов
		changeDataInFilesList: (state) => {
			// если есть список документов
			if (Array.isArray(state.filesList.data)) {
				state.filesList.data = state.filesList.data.map((fileItem) => {
					// меняем название и категорию активного файла
					if (fileItem.id === state.document.fileId && state.document.fileName && state.document.category) {
						return { ...fileItem, title: state.document.fileName, category: state.document.category };
					} else return fileItem;
				});
			}
		},
		// замена очищенного текста в документе
		replaceTextInDocument: (state, action: PayloadAction<string>) => {
			state.document.data = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.filesList = initialState.filesList;
			state.document = initialState.document;
			state.addingFile = initialState.addingFile;
			state.editingFile = initialState.editingFile;
			state.deletingFile = initialState.deletingFile;

			state.annotationGeneration = initialState.annotationGeneration;
			state.replaceInDocument = initialState.replaceInDocument;
			state.editingPhrases = initialState.editingPhrases;
		},
		// очистка списка файлов
		clearFilesList: (state) => {
			state.filesList = initialState.filesList;
		},
		// очистка данных документа
		clearDocument: (state) => {
			state.document = initialState.document;
		},
		// очистка статуса добавления файла
		clearAddFileStatus: (state) => {
			state.addingFile = initialState.addingFile;
		},
		// очистка статуса изменения файла
		clearEditingFileStatus: (state) => {
			state.editingFile = initialState.editingFile;
		},
		// очистка статуса удаления файла
		clearDeletingFileStatus: (state) => {
			state.deletingFile = initialState.deletingFile;
		},
		// очистка генерации аннотации
		clearAnnotationGeneration: (state) => {
			state.annotationGeneration = initialState.annotationGeneration;
		},
		// очистка данных замены текста в документах
		clearReplaceInDocument: (state) => {
			state.replaceInDocument = initialState.replaceInDocument;
		},
		// очистка данных замены фраз в документах
		clearEditingPhrases: (state) => {
			state.editingPhrases = initialState.editingPhrases;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getFilesList.pending, (state) => {
				state.filesList.status = RequestStatus.LOADING;
			})
			.addCase(getFilesList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "docs" in action.payload && Array.isArray(action.payload.docs)) {
					state.filesList.status = RequestStatus.IDLE;
					state.filesList.data = action.payload.docs.sort((a, b) => {
						if (a.title > b.title) return 1;
						else if (a.title < b.title) return -1;
						else return 0;
					});
					// кол-во доков в категориях
					const numberOfDocumentsInCategories: Record<string, number> = {};
					action.payload.docs.forEach(({ category }) => {
						if (category === null) return;
						if (category in numberOfDocumentsInCategories) numberOfDocumentsInCategories[category] += 1;
						else numberOfDocumentsInCategories[category] = 1;
					});
					state.filesList.numberOfDocumentsInCategories = numberOfDocumentsInCategories;
				} else state.filesList.status = RequestStatus.FAILED;
			})
			.addCase(getFilesList.rejected, (state, action: PayloadAction<unknown>) => {
				state.filesList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.filesList.data = { error, message };
			})
			.addCase(getFile.pending, (state) => {
				state.document.status = RequestStatus.LOADING;
			})
			.addCase(getFile.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "text" in action.payload) {
					state.document.status = RequestStatus.IDLE;
					state.document.data = action.payload.text;
					state.document.glossary = action.payload.glossary;
					state.document.useHead = action.payload.useHead;
					state.document.useTitle = action.payload.useTitle;
					state.document.useAnnotation = action.payload.useAnnotation;
					state.document.useAnchors = action.payload.useAnchors;
					state.document.annotation = action.payload.annotation;
				} else state.document.status = RequestStatus.FAILED;
			})
			.addCase(getFile.rejected, (state, action: PayloadAction<unknown>) => {
				state.document.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.document.data = { error, message };
			})
			.addCase(addFile.pending, (state) => {
				state.addingFile.status = RequestStatus.LOADING;
			})
			.addCase(addFile.fulfilled, (state, action) => {
				state.addingFile.status = RequestStatus.IDLE;
				if (action.payload) {
					state.addingFile.error = action.payload.error;
					state.addingFile.message = action.payload.message;
					if (action.payload.id) state.addingFile.id = action.payload.id;
				}
			})
			.addCase(addFile.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingFile.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingFile.error = error;
				state.addingFile.message = message;
			})
			.addCase(editFile.pending, (state) => {
				state.editingFile.status = RequestStatus.LOADING;
			})
			.addCase(editFile.fulfilled, (state, action) => {
				state.editingFile.status = RequestStatus.IDLE;
				if (action.payload) {
					state.editingFile.error = action.payload.error;
					state.editingFile.message = action.payload.message;
				}
			})
			.addCase(editFile.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingFile.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingFile.error = error;
				state.editingFile.message = message;
			})
			.addCase(deleteFile.pending, (state) => {
				state.deletingFile.status = RequestStatus.LOADING;
			})
			.addCase(deleteFile.fulfilled, (state, action) => {
				state.deletingFile.status = RequestStatus.IDLE;
				if (action.payload) {
					state.deletingFile.error = action.payload.error;
					state.deletingFile.message = action.payload.message;
				}
			})
			.addCase(deleteFile.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingFile.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingFile.error = error;
				state.deletingFile.message = message;
			})
			.addCase(generateAnnotation.pending, (state) => {
				state.annotationGeneration.status = RequestStatus.LOADING;
			})
			.addCase(generateAnnotation.fulfilled, (state, action) => {
				if (action.payload?.annotation) {
					state.annotationGeneration.status = RequestStatus.IDLE;
					state.annotationGeneration.text = action.payload.annotation;
				} else {
					state.annotationGeneration.status = RequestStatus.FAILED;
				}
			})
			.addCase(generateAnnotation.rejected, (state, action: PayloadAction<unknown>) => {
				const { error, message, isAbortError } = getResponseError(action);
				if (isAbortError) {
					state.annotationGeneration.status = RequestStatus.IDLE;
				} else {
					state.annotationGeneration.status = RequestStatus.FAILED;
					state.annotationGeneration.error = error;
					state.annotationGeneration.message = message;
				}
			})
			.addCase(replaceTextInAllDocuments.pending, (state) => {
				state.replaceInDocument.status = RequestStatus.LOADING;
			})
			.addCase(replaceTextInAllDocuments.fulfilled, (state, action) => {
				if (action.payload) {
					state.replaceInDocument.status = RequestStatus.IDLE;
					state.replaceInDocument.error = action.payload.error;
					state.replaceInDocument.message = action.payload.message;
					if ("replacements" in action.payload && typeof action.payload.replacements === "number") {
						state.replaceInDocument.replacements = action.payload.replacements;
					}
				} else state.replaceInDocument.status = RequestStatus.FAILED;
			})
			.addCase(replaceTextInAllDocuments.rejected, (state, action: PayloadAction<unknown>) => {
				state.replaceInDocument.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.replaceInDocument.error = error;
				state.replaceInDocument.message = message;
			})
			.addCase(modifyPhrases.pending, (state) => {
				state.editingPhrases.status = RequestStatus.LOADING;
			})
			.addCase(modifyPhrases.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingPhrases.status = RequestStatus.IDLE;
					state.editingPhrases.error = action.payload.error;
					state.editingPhrases.message = action.payload.message;
				} else state.editingPhrases.status = RequestStatus.FAILED;
			})
			.addCase(modifyPhrases.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingPhrases.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingPhrases.error = error;
				state.editingPhrases.message = message;
			});
	},
});

export const {
	changeProgressAddingFile,
	addActiveFileId,
	changeActiveFileName,
	changeActiveFileCategory,
	editDocument,
	editAnnotation,
	changeDocumentBooleanValues,
	editGlossaryDataCellAbbreviation,
	editGlossaryDataCellTranscript,
	addRow,
	addRows,
	replaceGlossary,
	deleteRows,
	changeDataInFilesList,
	replaceTextInDocument,

	clearState,
	clearFilesList,
	clearDocument,
	clearAddFileStatus,
	clearEditingFileStatus,
	clearDeletingFileStatus,
	clearAnnotationGeneration,
	clearReplaceInDocument,
	clearEditingPhrases,
} = qasDocumentSlice.actions;

export const selectFilesList = (state: RootState) => state.qasDocument.filesList;
export const selectDocument = (state: RootState) => state.qasDocument.document;
export const selectAddingFileStatus = (state: RootState) => state.qasDocument.addingFile;
export const selectEditingFileStatus = (state: RootState) => state.qasDocument.editingFile;
export const selectDeletingFileStatus = (state: RootState) => state.qasDocument.deletingFile;
export const selectAnnotationGeneration = (state: RootState) => state.qasDocument.annotationGeneration;
export const selectReplaceInDocument = (state: RootState) => state.qasDocument.replaceInDocument;
export const selectEditingPhrases = (state: RootState) => state.qasDocument.editingPhrases;

export default qasDocumentSlice.reducer;
